/**
 * DataReachExplainer — progressive-disclosure explainer for DATA.REACH (D2).
 *
 * DATA.REACH is the opfibration side of the DATA.LINEAGE dual pair. It
 * neutralizes the vulnerability class "data flows to a destination below
 * its sensitivity" by making the leak morphism structurally inexpressible:
 * opcartesian pushforward only carries a value upward in the sensitivity
 * order, so the downgrading flow is not a closed term in the kernel.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { DATA_REACH_META } from "@csk/core";
import { THEOREM_COLOR, withAlpha } from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";
import {
  ExplainerCard,
  FibrationDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { TheoremMetaStrip } from "./TheoremMetaStrip.js";
import { TIER_ONE_IMMUNITY_LINK } from "./tierOneCrossLinks.js";

const ACCENT = THEOREM_COLOR["DATA.REACH"];

const IDENTITY: ExplainerIdentity = {
  id: "explainer-data-reach",
  kind: "SSM · Neutralization Theorem (S1)",
  title: "DATA.REACH",
  subtitle: "D2 · Opfibration · IMMUNE · G2 Knowledge",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Opfibration (structure)", href: "#explainer-fibration", accent: ACCENT },
  {
    label: "DATA.ORIGIN (dual)",
    href: "#explainer-data-origin",
    accent: THEOREM_COLOR["DATA.ORIGIN"],
  },
  { label: "SEPARATE (G2 sibling)", href: "#explainer-separate", accent: THEOREM_COLOR.SEPARATE },
  TIER_ONE_IMMUNITY_LINK,
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>DATA.REACH</strong> says: data can only reach a destination of equal-or-higher
      sensitivity, by construction. A value that flows somewhere it should not is not a value you
      can write down.
    </p>
  ),
  intuition: (
    <>
      <p>
        Information leaks are the mirror image of provenance forgery: not "where did this come
        from?" but "where is this allowed to go?". The usual answer is a runtime taint check that
        watches each flow and trips on a downgrade — a story about catching the leak after it is
        expressible.
      </p>
      <p>
        CSK organizes flow as an opfibration over a base of sensitivity levels. The opcartesian
        pushforward carries a value <em>up</em> the order; there is no map carrying it down. The
        leaking flow is therefore unconstructable in the source rather than caught at runtime.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Let <MathInline>{tex`\pi : E \to B`}</MathInline> be the sensitivity opfibration,{" "}
        <MathInline>{tex`B`}</MathInline> the poset of sensitivity levels. For a base morphism{" "}
        <MathInline>{tex`f : A \to B`}</MathInline> and an object <MathInline>{tex`e`}</MathInline>{" "}
        over <MathInline>{tex`A`}</MathInline>, the opcartesian pushforward{" "}
        <MathInline>{tex`f_{+}(e)`}</MathInline> is the universal value above{" "}
        <MathInline>{tex`B`}</MathInline> that any forward flow factors through.
      </p>
      <p style={{ marginTop: "0.5rem" }}>
        A leak would be a morphism whose projection is{" "}
        <MathInline>{tex`\mathrm{sens}(\mathrm{dst}) < \mathrm{sens}(\mathrm{src})`}</MathInline>.
        The opfibration admits no such arrow: every flow factors through an opcartesian pushforward
        that is monotone in the sensitivity order. With <code>DATA.ORIGIN</code> it forms the{" "}
        <code>DATA.LINEAGE</code> dual pair — both are projections of the Categorical Immunity
        Theorem, not separate stories.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Vulnerability class neutralized:</strong> sensitive-data exposure to a
        lower-sensitivity sink — logs, errors, side channels (CWE-200, CWE-209, CWE-532). Realized
        by <em>structural inexpressibility</em>: the downgrading flow is not a closed term in the
        kernel category.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> If you serialize a value to bytes and re-emit it
        through an unindexed channel, you have left <code>E</code>. DATA.REACH is then vacuous on
        that channel — the witness has gone away, not been bypassed.
      </p>
      <p>
        <strong>Seam type</strong> (per <code>theory/structural/SEAM-THEORY.md</code>):{" "}
        {DATA_REACH_META.seamType}. <strong>Bridge</strong>: declassification witness (governance
        artifact).
      </p>
    </>
  ),
  references: [
    {
      label: "DATA.REACH theorem card",
      path: "docs/reference/theorem-cards/DATA-REACH.md",
    },
    {
      label: "Kernel module (main theorem REACH-neutralized)",
      path: `agda/${DATA_REACH_META.agdaSource}`,
      note: `DATA_REACH_META.agdaSource is "${DATA_REACH_META.agdaSource}"; the repo path is prefixed with "agda/". Main theorem at lines 105–204.`,
    },
    {
      label: "Theorem Canon (§4.1 IMMUNE mode details; §6 DATA.LINEAGE)",
      path: "docs/reference/Theorem-Canon.md",
    },
  ],
};

const DIAGRAM = (
  <>
    <TheoremMetaStrip
      theoremName="DATA.REACH"
      code="D2"
      structure="Opfibration"
      mode="immune"
      generator="G2"
      seamType={DATA_REACH_META.seamType}
      bridgeArtifact="declassification witness"
      theoremColor={ACCENT}
      washColor={withAlpha(ACCENT, "1A")}
    />
    <div style={{ marginTop: "0.5rem" }}>
      <FibrationDiagram
        accent={ACCENT}
        ariaLabel="Sensitivity opfibration π : E → B with opcartesian pushforward above the base flow f"
      />
    </div>
  </>
);

export function DataReachExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
